import { useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarClockIcon, PauseIcon, SkipForwardIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { useSite } from '../../../contexts/SiteContext';
import { dayShort, nextIsGift, shiftDate } from '../../../utils/account';
import { POSTPONE_DAYS, canChangeDelivery } from '../../../core/rules/subscription';
import { ease } from '../../../core/format/motion';
import type { SubscriptionAction } from '../../../core/types/requests';
import { btnOutline, chipCls } from '../../../ui/styles';

/**
 * Opschuiven, overslaan en pauzeren: één tik, altijd met "ongedaan maken".
 * Hoe makkelijker pauzeren is, hoe minder mensen opzeggen.
 */
export function DeliveryQuickActions() {
  const {
    policy
  } = useSite();
  const {
    account,
    changeSubscription
  } = useSignedInAccount();
  const reduce = useReducedMotion();
  const panelId = useId();
  const [pausing, setPausing] = useState(false);
  const [busy, setBusy] = useState(false);
  const sub = account.subscription;
  if (!sub || sub.status !== 'actief') return null;
  // Opschuiven en overslaan gaan over déze levering; na de termijn ligt die vast. Pauzeren kan altijd.
  const locked = !canChangeDelivery(sub.nextDelivery, policy.subscription);

  /** Melding pas als het gelukt is, met "ongedaan maken" voor precies deze actie. Eén actie tegelijk. */
  const withUndo = async (action: SubscriptionAction, title: string, description: string) => {
    if (busy) return;
    setBusy(true);
    const undo = await changeSubscription(action);
    setBusy(false);
    if (!undo) return;
    toast.success(title, {
      description,
      action: {
        label: 'Ongedaan maken',
        onClick: () => void undo()
      }
    });
  };

  // Drie gelijke knoppen naast elkaar (op de telefoon onder elkaar).
  const btn = btnOutline;
  return <div data-id="element-5935" data-mp="33ja6y7uwri5h">
      <div className="grid gap-2 sm:grid-cols-3" data-id="element-5936" data-mp="33j0n720gi788">
        <button type="button" className={btn} disabled={busy || locked} onClick={() => void withUndo({
        type: 'postpone',
        days: POSTPONE_DAYS
      }, 'Een week later', `Je levering komt nu op ${dayShort(shiftDate(sub.nextDelivery, POSTPONE_DAYS))}.`)} data-id="element-5937" data-mp="ans835x4uqtp">
          <CalendarClockIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-5938" data-mp="fphgsewvusem" />
          Schuif 1 week op
        </button>
        <button type="button" className={btn} disabled={busy || locked} onClick={() => void withUndo({
        type: 'skip'
      }, 'Levering overgeslagen', `De volgende komt ${sub.intervalWeeks} weken later.${nextIsGift(sub) ? ' Je cadeau schuift mee.' : ''}`)} data-id="element-5939" data-mp="ookybegeizr0">
          <SkipForwardIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-5940" data-mp="17et0nuca1m65" />
          Sla deze over
        </button>
        <button type="button" className={btn} disabled={busy} aria-expanded={pausing} aria-controls={panelId} onClick={() => setPausing(v => !v)} data-id="element-5941" data-mp="1r1dzhi4a72lr">
          <PauseIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-5942" data-mp="1hzehonlfzuhr" />
          Pauzeer
        </button>
      </div>
      {locked && <p className="mt-2 text-[13px] text-muted" data-id="element-5943" data-mp="1lzeygew0khrz">
          Opschuiven of overslaan kan tot {policy.subscription.changeUntilDays} dagen voor de levering. Deze is al onderweg naar de inpaktafel.
        </p>}

      <AnimatePresence initial={false} data-id="element-5944" data-mp="1d2ecsg0bwxs8">
        {pausing && <motion.div id={panelId} initial={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} animate={reduce ? {
        opacity: 1
      } : {
        height: 'auto',
        opacity: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease
      }} className="overflow-hidden" data-id="element-5945" data-mp="ycgntxuks41z">
            <fieldset className="mt-3 bg-sand p-4" data-id="element-5946" data-mp="m9nmqk4tjfdf">
              <legend className="float-left text-[14px] text-ink" data-id="element-5947" data-mp="m8co0yq8cxt6">Hoe lang wil je pauzeren?</legend>
              <p className="clear-left pt-0.5 text-[13px] text-muted" data-id="element-5948" data-mp="hat4bjarjstu">Daarna gaat het vanzelf verder. Eerder hervatten kan altijd.</p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-5949" data-mp="8hzbsk6b2wy1">
                {policy.subscription.pauseOptions.map(o => <button key={o.weeks} type="button" disabled={busy} onClick={() => {
              setPausing(false);
              void withUndo({
                type: 'pause',
                weeks: o.weeks
              }, `Gepauzeerd voor ${o.label}`, 'We sturen je een seintje een week voordat het weer begint.');
            }} className={`${chipCls(false)} bg-shell text-ink disabled:opacity-50`} data-id="element-5950" data-mp="3iwghrg4o539">
                    {o.label}
                  </button>)}
              </div>
            </fieldset>
          </motion.div>}
      </AnimatePresence>
    </div>;
}